// 세금계산서 현황 조회 + 건별/일괄 취소 + 삭제 + 재배치 진입.
//
// OrderBillReviewPanel(순수 표시)과 짝을 이룬다. 두 군데서 그대로 재사용한다
// (사용자 요구 2026-10-21):
//   - 수주품의서 관리 목록 하위 항목(mode="manage") - ProjectOrdersListContainer
//   - 재작성 가드 다이얼로그(mode="guard") - ProjectDetailInfoContainer 등 원가산출 해제 화면
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import * as billCancelApi from '../../lib/api/billCancel';
import * as billCollectApi from '../../lib/api/billCollect';
import * as billCollectPlanApi from '../../lib/api/billCollectPlan';
import * as orderApi from '../../lib/api/order';
import * as billReplanApi from '../../lib/api/billReplan';
import * as approvalV2Api from '../../lib/api/approvalV2';
import AskDialog from '../../components/common/dialog/AskDialog';
import OrderBillReviewPanel from '../../components/project_orders/OrderBillReviewPanel';
import BillCollectAddDialog from '../../components/project_orders/BillCollectAddDialog';
import BillPlanEditDialog from '../../components/project_orders/BillPlanEditDialog';
import BillPlanReplanContainer from '../projectmng/billplan/BillPlanReplanContainer';

/** 취소 사유 입력. 건별 수금 취소(BillCollectService.cancelCollect)는 사유가 필수다 - 금액
 *  이력이라 "왜 취소했는지"가 남아야 한다. */
const ReasonDialog = ({ open, title, required, onCancel, onConfirm }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label={required ? '취소 사유 (필수)' : '취소 사유 (선택)'}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>취소</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={required && !reason.trim()}
          onClick={() => onConfirm(reason.trim())}
        >
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** 수주품의서 1건 분량. 목록/가드 다이얼로그 안에서 여러 개 나란히 쓸 수 있게 분리했다. */
export const OrderBillReviewSection = ({
  roUuid,
  initialReview,
  mode = 'manage',
  onDeleted,
  onChanged,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [review, setReview] = useState(initialReview || null);
  const [loading, setLoading] = useState(!initialReview);
  const [rowBusyUuid, setRowBusyUuid] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [replanOpen, setReplanOpen] = useState(false);
  // 건별 수금 취소(BillCollectService 포팅, 2026-10-21) - "이 건만 취소"는 사유 필수.
  const [collectReasonTarget, setCollectReasonTarget] = useState(null);
  // 수금 등록 / 계획 관리(2026-10-21 수금/지급 계획 N개).
  const [addCollectTarget, setAddCollectTarget] = useState(null);
  const [addCollectSubmitting, setAddCollectSubmitting] = useState(false);
  const [managePlansTarget, setManagePlansTarget] = useState(null);
  const [managePlansSubmitting, setManagePlansSubmitting] = useState(false);

  const showError = useCallback(
    (err, fallback) => {
      const message = err?.response?.data?.message || err?.message || fallback;
      enqueueSnackbar(message, { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const load = useCallback(async () => {
    if (!roUuid) return;
    setLoading(true);
    try {
      const res = await billCancelApi.getOrderBillReview(roUuid);
      setReview(res?.data?.data || null);
    } catch (err) {
      showError(err, '계산서 현황을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [roUuid, showError]);

  // 가드 다이얼로그는 재작성 거부 응답(409)에 이미 실린 review를 초기값으로 받는다 -
  // 따로 또 불러올 필요가 없다. 관리 패널은 roUuid만 받으므로 직접 불러온다.
  useEffect(() => {
    if (!initialReview) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runRowAction = useCallback(
    async (row, apiFn, successMsg) => {
      setRowBusyUuid(row.invUuid);
      try {
        await apiFn(row.invUuid);
        enqueueSnackbar(successMsg, { variant: 'success' });
        await load();
        onChanged?.();
      } catch (err) {
        showError(err, '처리에 실패했습니다.');
      } finally {
        setRowBusyUuid(null);
      }
    },
    [load, onChanged, enqueueSnackbar, showError],
  );

  const handleCancelCollection = (row) =>
    runRowAction(row, (invUuid) => billCancelApi.cancelCollection(invUuid), '수금(지급)을 취소했습니다.');
  const handleCancelIssueComplete = (row) =>
    runRowAction(row, billCancelApi.cancelIssueComplete, '발행을 취소했습니다.');
  const handleCancelIssueRequest = (row) =>
    runRowAction(row, billCancelApi.cancelIssueRequest, '발행요청을 취소했습니다.');

  // 결재 회수(세금계산서 발행 요청 결재 스위치, 2026-10-21) - 이 회차가 "결재중"일 때만
  // 노출된다(row.trjUuid = 결재문서ID). 상신자 또는 관리자만 가능(ApprovalV2Service.recallDocument).
  const handleRecallApproval = (row) =>
    runRowAction(
      row,
      () => approvalV2Api.recallApprovalV2Document(row.trjUuid),
      '발행 요청 결재를 회수했습니다. "예정" 상태로 되돌아갑니다.',
    );

  // 수금 내역 1건만 취소. 한 계산서에 N번 나눠 들어올 수 있어(부분수금) 전체 취소와 별개 경로다.
  const handleCancelCollectRecord = (collect, row) => setCollectReasonTarget({ collect, row });
  const handleConfirmCancelCollectRecord = async (reason) => {
    const target = collectReasonTarget;
    setCollectReasonTarget(null);
    if (!target) return;
    setRowBusyUuid(target.row.invUuid);
    try {
      await billCollectApi.cancelCollectRecord(target.collect.collectUuid, reason);
      enqueueSnackbar('수금(지급) 내역을 취소했습니다.', { variant: 'success' });
      await load();
      onChanged?.();
    } catch (err) {
      showError(err, '수금 내역 취소에 실패했습니다.');
    } finally {
      setRowBusyUuid(null);
    }
  };

  // 수금(지급) 등록 - 자동배분/수동/미배정 전부 BillCollectService.addCollect 한 곳을 거친다.
  const handleAddCollect = (row) => setAddCollectTarget(row);
  const handleSubmitAddCollect = async (payload) => {
    const target = addCollectTarget;
    if (!target) return;
    setAddCollectSubmitting(true);
    try {
      await billCollectApi.addCollect(target.invUuid, payload);
      enqueueSnackbar('수금(지급)을 등록했습니다.', { variant: 'success' });
      setAddCollectTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      showError(err, '수금(지급) 등록에 실패했습니다.');
    } finally {
      setAddCollectSubmitting(false);
    }
  };

  // 계획 관리(선금/중도금/잔금 N개로 쪼개기/다시 합치기) - BillCollectPlanService.replacePlans.
  const handleManagePlans = (row) => setManagePlansTarget(row);
  const handleSavePlans = async (plans) => {
    const target = managePlansTarget;
    if (!target) return;
    setManagePlansSubmitting(true);
    try {
      await billCollectPlanApi.replacePlans(target.invUuid, plans);
      enqueueSnackbar('계획을 저장했습니다.', { variant: 'success' });
      setManagePlansTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      showError(err, '계획 저장에 실패했습니다.');
    } finally {
      setManagePlansSubmitting(false);
    }
  };

  // 미배정 수금을 계획에 수동 연결.
  const handleLinkPlan = async (collect, row, planUuid) => {
    try {
      await billCollectApi.linkPlan(collect.collectUuid, planUuid);
      enqueueSnackbar('계획에 연결했습니다.', { variant: 'success' });
      await load();
      onChanged?.();
    } catch (err) {
      showError(err, '계획 연결에 실패했습니다.');
    }
  };

  const handleBulkCancel = async () => {
    setConfirmBulk(false);
    setBulkBusy(true);
    try {
      await billCancelApi.bulkCancelOrder(roUuid, '권한자 일괄 취소');
      enqueueSnackbar('일괄 취소를 완료했습니다.', { variant: 'success' });
      await load();
      onChanged?.();
    } catch (err) {
      showError(err, '일괄 취소에 실패했습니다.');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDelete = async () => {
    try {
      await orderApi.deleteOrderContract(roUuid);
      enqueueSnackbar('수주품의서를 삭제했습니다.', { variant: 'success' });
      onDeleted?.();
      onChanged?.();
    } catch (err) {
      showError(err, '삭제에 실패했습니다.');
    }
  };

  const bulkCancelSummary = () => {
    if (!review) return '';
    const n = (num) => Number(num || 0).toLocaleString('ko-KR');
    return (
      `발행 ${(review.blockingRows || []).length}건을 포함해 발행액 ${n(review.issuedAmt)}원, ` +
      `수금(지급)액 ${n(review.collectedAmt)}원이 전부 취소됩니다.\n` +
      '되돌릴 수 없습니다. 계속할까요?'
    );
  };

  return (
    <Box>
      <OrderBillReviewPanel
        review={review}
        loading={loading}
        mode={mode}
        rowBusyUuid={rowBusyUuid}
        bulkBusy={bulkBusy}
        onCancelCollection={handleCancelCollection}
        onCancelCollectRecord={handleCancelCollectRecord}
        onCancelIssueComplete={handleCancelIssueComplete}
        onCancelIssueRequest={handleCancelIssueRequest}
        onRecallApproval={handleRecallApproval}
        onBulkCancel={() => setConfirmBulk(true)}
        onDelete={handleDelete}
        onToggleReplan={() => setReplanOpen((v) => !v)}
        replanOpen={replanOpen}
        onAddCollect={handleAddCollect}
        onManagePlans={handleManagePlans}
        onLinkPlan={handleLinkPlan}
      />
      <BillCollectAddDialog
        open={Boolean(addCollectTarget)}
        row={addCollectTarget}
        submitting={addCollectSubmitting}
        onClose={() => setAddCollectTarget(null)}
        onSubmit={handleSubmitAddCollect}
      />
      <BillPlanEditDialog
        open={Boolean(managePlansTarget)}
        row={managePlansTarget}
        submitting={managePlansSubmitting}
        onClose={() => setManagePlansTarget(null)}
        onSave={handleSavePlans}
      />
      <Collapse in={replanOpen} unmountOnExit>
        {review?.prjUuid && (
          <Box sx={{ mt: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
            <BillPlanReplanContainer
              prjUuid={review.prjUuid}
              entryPoint={billReplanApi.ENTRY_ORDER_LIST}
            />
          </Box>
        )}
      </Collapse>
      <AskDialog
        visible={confirmBulk}
        title="권한자 일괄 취소"
        description={bulkCancelSummary()}
        confirmText="일괄 취소"
        onConfirm={handleBulkCancel}
        onCancle={() => setConfirmBulk(false)}
      />
      <ReasonDialog
        open={Boolean(collectReasonTarget)}
        title={`수금(지급) 내역 취소 - ${collectReasonTarget?.collect?.collectDt || ''} ${
          collectReasonTarget ? Number(collectReasonTarget.collect.collectAmt || 0).toLocaleString('ko-KR') : ''
        }원`}
        required
        onCancel={() => setCollectReasonTarget(null)}
        onConfirm={handleConfirmCancelCollectRecord}
      />
    </Box>
  );
};

/**
 * 다이얼로그 래퍼.
 *   - roUuid 하나만 주면(수주품의서 관리) 그 건을 바로 조회한다.
 *   - initialReviews(OrderBillReviewDto 배열)를 주면(재작성 가드 409 응답) 그 값을 그대로 쓰고,
 *     이후 액션이 일어나면 해당 건만 다시 조회한다.
 */
const OrderBillReviewDialog = ({ open, onClose, roUuid, initialReviews, mode = 'manage', onChanged }) => {
  const sections =
    initialReviews && initialReviews.length > 0
      ? initialReviews.map((r) => ({ roUuid: r.roUuid, initialReview: r }))
      : roUuid
        ? [{ roUuid, initialReview: null }]
        : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        세금계산서 현황
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {sections.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.disabled' }}>대상이 없습니다.</Box>
        ) : (
          sections.map((s) => (
            <Box key={s.roUuid} sx={{ mb: 3 }}>
              <OrderBillReviewSection
                roUuid={s.roUuid}
                initialReview={s.initialReview}
                mode={mode}
                onDeleted={onClose}
                onChanged={onChanged}
              />
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderBillReviewDialog;
